import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { createUseStyles } from "react-jss";
import clsx from "clsx";
import dateformat from "dateformat";
import {
  Button as UIButton,
  CircularProgress,
  Collapse,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TextField,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import SaveIcon from "@mui/icons-material/Save";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import ArrowDownSLineIcon from "remixicon-react/ArrowDownSLineIcon";
import ArrowUpSLineIcon from "remixicon-react/ArrowUpSLineIcon";

import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { theme } from "$utils/theme/bataillog";
import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";
import useShippingRecord from "$hooks/useShippingRecord";
import useShippingRecordExport from "$hooks/useShippingRecordExport";

import {
  DispositionType,
  DeliveryType,
  PreparationOrder,
  ShippingRecord,
  ShippingRecordStatus,
} from "$types";

import ParcelTable from "$containers/Parcel/ParcelTable";
import { Button, Modal, Text } from "$components";
import JTable from "$components/JTable/JTable";

import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import {
  createDeliveryAddressFromOp,
  createShippingAddressFromOp,
  getPreparationOrderStatusLabelByRole,
  getShippingRecordDeliveryTypeLabel,
  getShippingRecordWishedDeliveryDate,
  PreparationOrderErrors,
} from "$utils/typeConstants";
import AssignCarrierPopin from "./AssignCarrierPopin";
import { queryClient } from "$utils/queryClient";
import { appState } from "$reducers";
import { getOpsWithParcel, setOpShippingRecordId } from "$services/Op.service";
import { CompanyServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { AssignCarrierFormValues } from "./AssignCarrierPopin/types";

type FormValues = Pick<
  ShippingRecord,
  | "notes"
  | "status"
  | "agencyCompanyId"
  | "commissionCompanyId"
  | "carrierCompanyId"
  | "deliveryType"
> & { opIds: string[] };

const mapShipmentsToOps = (
  shipments: ShippingRecord["shipments"] | undefined
): PreparationOrder[] =>
  shipments?.flatMap((shipment) =>
    shipment.op
      ? [
          {
            ...shipment.op,
            _id: shipment.op._id ?? shipment.opId,
            parcel: shipment.parcel,
          },
        ]
      : []
  ) ?? [];

type CarrierLabelProps = {
  carrierCompanyId: string;
  agencyCompanyId: string;
  deliveryType: DeliveryType;
};

function ShippingRecordItemCarrierLabel({
  carrierCompanyId,
  agencyCompanyId,
  deliveryType,
}: CarrierLabelProps) {
  const companyId = carrierCompanyId || agencyCompanyId || undefined;
  const { data: foundCompany } = useQuery(
    [CacheInvalidationAction.GET_COMPANY_BY_ID, companyId],
    () => CompanyServices.getCompanyById(companyId),
    {
      enabled: !!companyId,
      cacheTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const selectedCarrierCompanyLabel: string = useMemo(() => {
    if (foundCompany) {
      return `Transporteur : ${foundCompany?.name || "N/A"}`;
    }
    return "Affecter un transporteur";
  }, [foundCompany]);

  if (deliveryType === DeliveryType.PROVISIONING) {
    return <div>Transporteur : N/A</div>;
  }
  return <div>{selectedCarrierCompanyLabel}</div>;
}

type Props = {
  shippingRecord: ShippingRecord;
  expanded?: boolean;
};

function ShippingRecordItem({ shippingRecord, expanded = false }: Props) {
  const { userRole } = useAuth();
  const { filter: company } = useSelector(appState);

  const {
    shippingRecord: shippingRecordItem,
    isFetchingShippingRecord,
    fetchShippingRecord,
    updateShippingRecord,
    deleteShippingRecord,
  } = useShippingRecord(shippingRecord?.id, shippingRecord);
  const { exportShippingRecord } = useShippingRecordExport(shippingRecordItem);

  const defaultShippingRecordValues: FormValues = useMemo(
    () =>
      shippingRecordItem && Object.keys(shippingRecordItem).length
        ? {
            notes: shippingRecordItem.notes,
            opIds: shippingRecordItem.shipments?.map(
              (shipment) => shipment.opId
            ),
            status: shippingRecordItem.status,
            deliveryType: shippingRecordItem.deliveryType,
            carrierCompanyId: shippingRecordItem.carrierCompanyId,
            agencyCompanyId: shippingRecordItem.agencyCompanyId,
            commissionCompanyId: shippingRecordItem.commissionCompanyId,
          }
        : {
            notes: shippingRecord.notes,
            opIds: shippingRecord.shipments?.map((shipment) => shipment.opId),
            status: shippingRecord.status,
            deliveryType: shippingRecord.deliveryType,
            carrierCompanyId: shippingRecord.carrierCompanyId,
            agencyCompanyId: shippingRecord.agencyCompanyId,
            commissionCompanyId: shippingRecord.commissionCompanyId,
          },
    [shippingRecord, shippingRecordItem]
  );
  const { handleSubmit, register, reset, formState, setValue, watch } =
    useForm<FormValues>({
      defaultValues: defaultShippingRecordValues,
    });
  const deliveryType = watch("deliveryType");
  const carrierCompanyId = watch("carrierCompanyId");
  const agencyCompanyId = watch("agencyCompanyId");
  const commissionCompanyId = watch("commissionCompanyId");
  const isDeliveryTypeSelected = Boolean(watch("deliveryType"));
  const opIds = watch("opIds");

  const [isCollapsed, collapse] = useState<boolean>(expanded);
  const [isDeleteModalOpen, toggleDeleteModal] = useState<boolean>(false);
  const [isCarrierModalOpen, toggleCarrierModal] = useState<boolean>(false);
  const [opsInTable, setOPsInTable] = useState<PreparationOrder[]>(
    mapShipmentsToOps(shippingRecordItem?.shipments)
  );
  const [error, setError] = useState<PreparationOrderErrors>(undefined);

  const classes = useStyles();
  const { jclasses } = useJStyles();

  const isFormEdited: boolean = formState.isDirty;

  useEffect(() => {
    if (!error) {
      reset(defaultShippingRecordValues);
    }
  }, [reset, defaultShippingRecordValues, shippingRecordItem, error]);

  useEffect(() => {
    setOPsInTable(mapShipmentsToOps(shippingRecordItem?.shipments));
  }, [shippingRecordItem]);

  const onSubmitHandler = useCallback(
    async (shippingRecordFormValues: FormValues) => {
      setError(undefined);
      // TODO WARNING THIS IS NOT TEST
      let selectedOps;
      selectedOps = await queryClient.getQueryData([
        CacheInvalidationAction.GET_OPS_WITH_PARCEL,
        shippingRecordFormValues.opIds,
      ]);
      if (!selectedOps) {
        selectedOps = await queryClient.fetchQuery(
          [
            CacheInvalidationAction.GET_OPS_WITH_PARCEL,
            shippingRecordFormValues.opIds,
          ],
          () =>
            getOpsWithParcel({
              ...(company ? { company: company._id } : {}),
              $inId: shippingRecordFormValues.opIds,
            }),
          { cacheTime: 5 * 60 * 1000 }
        );
        await queryClient.setQueryData(
          [
            CacheInvalidationAction.GET_OPS_WITH_PARCEL,
            shippingRecordFormValues.opIds,
          ],
          selectedOps
        );
      }

      // An already selected OP was deleted
      if (selectedOps.some((selectedOp) => !selectedOp)) {
        return null;
      }
      // Carrier is selected but an OP has no refPreparationEntrepot
      if (
        !!shippingRecordFormValues.carrierCompanyId &&
        selectedOps.some((selectedOp) => !selectedOp.refPreparationEntrepot)
      ) {
        throw new Error(PreparationOrderErrors.OP_REF_PREPARATION_NOT_FOUND);
      }
      await updateShippingRecord(shippingRecordItem.id, {
        ...shippingRecordFormValues,
        shipments: selectedOps.map((op: PreparationOrder) => ({
          opId: op._id,
          parcelId: op.parcel.id,
          refPreparation: op.refPreparationEntrepot,
          fromAddress: createShippingAddressFromOp(op),
          toAddress: createDeliveryAddressFromOp(op),
        })),
      });
      setError(null);
      return null;
    },
    [updateShippingRecord, shippingRecordItem, company]
  );

  const handleShippingRecordValidation = useCallback(async () => {
    setValue("status", ShippingRecordStatus.TO_SHIP);
    try {
      await handleSubmit(onSubmitHandler)();
      // await fetchShippingRecords();
    } catch (error) {
      setError(error?.message);
    }
  }, [setValue, handleSubmit, onSubmitHandler]);

  const handleOPDelete = useCallback(
    async (opId: string) => {
      if (opIds.length === 1) {
        toggleDeleteModal(true);
        return;
      }
      const newOpArray: string[] = opIds.filter((op: string) => op !== opId);
      const removedOp: string = opIds.find((op: string) => op === opId);
      if (removedOp) {
        setOpShippingRecordId(removedOp, {
          shippingRecordId: null,
          status: 2,
        });
      }
      setValue("opIds", newOpArray, { shouldDirty: true });
      await handleSubmit(onSubmitHandler)();
    },
    [opIds, setValue, handleSubmit, onSubmitHandler]
  );

  const handleCancel = useCallback(() => {
    fetchShippingRecord(shippingRecord.id);
  }, [fetchShippingRecord, shippingRecord]);

  const handleShippingRecordDelete = useCallback(async () => {
    toggleDeleteModal(false);
    await deleteShippingRecord(shippingRecord.id);
    // await fetchShippingRecords();
  }, [deleteShippingRecord, shippingRecord.id]);

  const assignCarrier = useCallback(
    async (assignCarrierFormValues: AssignCarrierFormValues) => {
      Object.keys(assignCarrierFormValues).forEach(
        (key: keyof AssignCarrierFormValues) =>
          setValue(key, assignCarrierFormValues[key])
      );
      const submitResult = await handleSubmit(onSubmitHandler)();
      return submitResult;
    },
    [handleSubmit, onSubmitHandler, setValue]
  );

  const resetCarrier = useCallback(() => {
    // Not ideal since the 4 strings are hardcoded (see assignCarrier above which doesn't hardcode them: it's better)
    setValue("carrierCompanyId", undefined);
    setValue("commissionCompanyId", undefined);
    setValue("agencyCompanyId", undefined);
    setValue("deliveryType", undefined);
  }, [setValue]);

  const columns = useMemo(
    () => [
      {
        Header: "Entreprise",
        minWidth: 90,
        maxWidth: 90,
        Cell: ({
          row: { original: currentOP },
        }: {
          row: { original: PreparationOrder };
        }) => {
          return <Text>{currentOP?.company?.name || "N/A"}</Text>;
        },
      },
      {
        Header: "N° OP",
        minWidth: 70,
        maxWidth: 70,
        Cell: ({
          row: { original: currentOP },
        }: {
          row: { original: PreparationOrder };
        }) => <Text>{currentOP?.code || "N/A"}</Text>,
      },
      {
        Header: "Statut OP",
        minWidth: 70,
        maxWidth: 70,
        Cell: ({
          row: { original: currentOP },
        }: {
          row: { original: PreparationOrder };
        }) => (
          <Text>
            {currentOP?.status != null
              ? getPreparationOrderStatusLabelByRole(userRole, currentOP.status)
              : "N/A"}
          </Text>
        ),
      },
      {
        Header: "Lieu enlèvement",
        minWidth: 120,
        maxWidth: 120,
        Cell: ({
          row: { original: currentOP },
        }: {
          row: { original: PreparationOrder };
        }) => (
          <div className={classes.cell}>
            {currentOP && currentOP.entrepot ? (
              <>
                <div>
                  <Text type="important" className={classes.cell}>
                    {currentOP?.entrepot?.name ?? ""}
                  </Text>
                </div>
                <div>{`${currentOP?.entrepot?.location.cp ?? ""} ${
                  currentOP?.entrepot?.location.city ?? ""
                }`}</div>
              </>
            ) : (
              "N/A"
            )}
          </div>
        ),
      },

      {
        Header: "Lieu livraison",
        minWidth: 120,
        maxWidth: 120,
        Cell: ({
          row: { original: currentOP },
        }: {
          row: { original: PreparationOrder };
        }) =>
          currentOP?.shipType === DispositionType.ON_DOCKYARD ? (
            <div className={classes.cell}>
              {currentOP && (currentOP.chantier || currentOP.dockyard) ? (
                <>
                  <div>
                    <Text type="important" className={classes.cell}>
                      {currentOP?.chantier?.code ?? currentOP?.dockyard?.code}
                    </Text>
                    {` ${
                      currentOP?.chantier?.name ?? currentOP?.dockyard?.name
                    }`}
                  </div>
                  <div>
                    {[currentOP?.shipPostalCode, currentOP?.shipCity]
                      .filter(Boolean)
                      .join(" ") || "N/A"}
                  </div>
                </>
              ) : (
                "N/A"
              )}
            </div>
          ) : (
            <div className={classes.cell}>
              <div>
                <Text type="important" className={classes.cell}>
                  {currentOP?.shipEntrepotName || currentOP?.shipPlace || "N/A"}
                </Text>
              </div>
              <div>
                {[currentOP?.shipPostalCode, currentOP?.shipCity]
                  .filter(Boolean)
                  .join(" ") || "N/A"}
              </div>
            </div>
          ),
      },
      {
        Header: "Date de livraison souhaitée",
        minWidth: 90,
        maxWidth: 90,
        Cell: ({
          row: { original: currentOP },
        }: {
          row: { original: PreparationOrder };
        }) => (
          <Text>
            {currentOP?.edb?.dispositionDate
              ? dateformat(currentOP?.edb?.dispositionDate, DEFAULT_DATE_FORMAT)
              : "N/A"}
          </Text>
        ),
      },
      {
        Header: null,
        accessor: "expander",
        minWidth: 60,
        maxWidth: 60,
        preventClickAction: true,
        Cell: ({ row }: any) => (
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <span
              {...row.getToggleRowExpandedProps()}
              style={{
                minWidth: 30,
                padding: 3,
                marginLeft: 4,
                position: "relative",
                top: 2,
              }}
            >
              {row.isExpanded ? <ArrowUpSLineIcon /> : <ArrowDownSLineIcon />}
            </span>
            <Button
              color="primary"
              variant="text"
              className={classes.deleteButton}
              style={{ minWidth: 50 }}
              onClick={() => handleOPDelete(row.original._id)}
            >
              <DeleteForeverOutlinedIcon />
            </Button>
          </div>
        ),
      },
    ],
    [userRole, handleOPDelete, classes]
  );

  const renderRowSubComponent = useCallback(
    ({ row }) => {
      const op: PreparationOrder = row.original;
      return (
        <div className={classes.subRow}>
          <div className={classes.opParcelTitle}>Colisage</div>
          <div className={classes.parcelTable}>
            <ParcelTable parcel={op.parcel} showDetailsTable />
          </div>
        </div>
      );
    },
    [classes]
  );

  if (isFetchingShippingRecord || !shippingRecordItem) {
    return (
      <Paper className={classes.mainContainer}>
        <div className={classes.titlesContainer}>
          <div
            className={classes.recordCode}
          >{`DOSSIER N° ${shippingRecord.code}`}</div>
          <UIButton
            onClick={() => collapse(!isCollapsed)}
            style={{ minWidth: 25, padding: 8 }}
          >
            {isCollapsed ? <ArrowUpSLineIcon /> : <ArrowDownSLineIcon />}
          </UIButton>
        </div>
        <div
          className={classes.spinnerContainer}
          style={!isCollapsed ? { height: 600 } : {}}
        >
          <CircularProgress />
        </div>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        className={
          isFormEdited
            ? clsx(classes.mainContainer, classes.editedForm)
            : classes.mainContainer
        }
      >
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          <div>
            <div className={classes.titlesContainer}>
              <div>
                <div
                  className={classes.recordCode}
                >{`DOSSIER N° ${shippingRecordItem.code}`}</div>
                <div className={classes.deliveryDate}>
                  {(() => {
                    const wishedDeliveryDate =
                      getShippingRecordWishedDeliveryDate(shippingRecordItem);
                    return `Date de livraison souhaitée : ${
                      wishedDeliveryDate
                        ? dateformat(wishedDeliveryDate, DEFAULT_DATE_FORMAT)
                        : ""
                    }`;
                  })()}
                </div>
              </div>
              <div className={classes.textButtonsContainer}>
                <Button
                  isDefault
                  variant="text"
                  className={classes.textButton}
                  onClick={() => toggleCarrierModal(true)}
                >
                  <div className={classes.textAlignStart}>
                    <ShippingRecordItemCarrierLabel
                      carrierCompanyId={carrierCompanyId}
                      agencyCompanyId={agencyCompanyId}
                      deliveryType={deliveryType}
                    />
                    <Text type="important">
                      {getShippingRecordDeliveryTypeLabel(deliveryType)}
                    </Text>
                  </div>
                </Button>
                <Button
                  onClick={exportShippingRecord}
                  isDefault
                  variant="text"
                  className={classes.textButton}
                >
                  Exporter le dossier
                </Button>
                <Button
                  isDefault
                  variant="text"
                  className={classes.textButton}
                  onClick={() => toggleDeleteModal(true)}
                >
                  Supprimer le dossier
                </Button>
                <UIButton
                  onClick={() => collapse(!isCollapsed)}
                  style={{ minWidth: 25, padding: 8 }}
                >
                  {isCollapsed ? <ArrowUpSLineIcon /> : <ArrowDownSLineIcon />}
                </UIButton>
              </div>
            </div>
          </div>
          <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
            <div className={classes.opTableContainer}>
              <JTable
                data={opsInTable}
                columns={columns}
                setFilter={() => {}}
                setPageIndex={() => {}}
                renderRowSubComponent={renderRowSubComponent}
                hideFilters
                hideCheckboxes
                pageCount={1}
                pageIndex={0}
              />
            </div>
            <div>
              <div className={classes.totalParcelContainer}>
                <div className={classes.totalParcelTitle}>COLISAGE TOTAL</div>
                <TableContainer className={classes.totalParcelTable}>
                  <TableHead className={classes.tableHead}>
                    <TableCell width={120} className={classes.opTableHeadCell}>
                      Mètre plancher
                    </TableCell>
                    <TableCell width={120} className={classes.opTableHeadCell}>
                      Hauteur (cm)
                    </TableCell>
                    <TableCell width={120} className={classes.opTableHeadCell}>
                      Quantité
                    </TableCell>
                    <TableCell width={120} className={classes.opTableHeadCell}>
                      Poids total (kg)
                    </TableCell>
                  </TableHead>
                  <TableBody>
                    <TableCell className={classes.cell}>
                      {shippingRecordItem.totalParcel?.loadingMeters != null ? (
                        <NumericFormat
                          displayType="text"
                          decimalScale={2}
                          value={shippingRecordItem.totalParcel.loadingMeters}
                        />
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {shippingRecordItem.totalParcel?.height ?? "N/A"}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {shippingRecordItem.totalParcel?.quantity ?? "N/A"}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {shippingRecordItem.totalParcel?.weight ?? "N/A"}
                    </TableCell>
                  </TableBody>
                </TableContainer>
              </div>
              <div className={classes.notes}>
                <TextField
                  id="filled-multiline-static"
                  label="Remarques"
                  multiline
                  variant="outlined"
                  maxRows={3}
                  minRows={3}
                  className={classes.notesField}
                  {...register("notes")}
                />
              </div>
            </div>
            {error ? <Text className={classes.errorField}>{error}</Text> : null}
            <div className={classes.buttonsContainer}>
              <Button
                variant="contained"
                disabled={!isFormEdited || isFetchingShippingRecord}
                onClick={handleCancel}
              >
                Annuler
              </Button>
              <div>
                <Button
                  type="submit"
                  disabled={!isFormEdited || isFetchingShippingRecord}
                  variant="outlined"
                  color="primary"
                  style={{ textTransform: "none", marginRight: 20 }}
                >
                  <SaveIcon />
                  Enregistrer en brouillon
                </Button>
                <Button
                  disabled={isFetchingShippingRecord || !isDeliveryTypeSelected}
                  variant="contained"
                  onClick={handleShippingRecordValidation}
                >
                  Valider le dossier
                </Button>
              </div>
            </div>
          </Collapse>
        </form>
      </Paper>

      <Modal
        title="Confirmation de suppression"
        textValidate="Oui"
        textCancel="Non"
        open={isDeleteModalOpen}
        onValidate={handleShippingRecordDelete}
        onCancel={() => toggleDeleteModal(false)}
      >
        <div className={jclasses.dialogContent} style={{ textAlign: "center" }}>
          <Text>
            Attention
            <br />
            Vous êtes sur le point de supprimer le dossier.
            <br />
            L’ensemble des OP concernés seront basculés sur le tableau
            “Traitement des OP”.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>

      <AssignCarrierPopin
        onClose={() => toggleCarrierModal(false)}
        assignCarrier={assignCarrier}
        defaultValues={{
          carrierCompanyId,
          agencyCompanyId,
          commissionCompanyId,
          deliveryType,
        }}
        isOpen={isCarrierModalOpen}
        resetCarrier={resetCarrier}
      />
    </>
  );
}

export default ShippingRecordItem;

const useStyles = createUseStyles(() => ({
  deleteButton: {
    color: "rgba(0, 0, 0) !important",
    border: "none !important",
    "&:hover": {
      color: "rgba(0, 0, 0) !important",
      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
    },
    "& svg": {
      color: "inherit !important",
    },
  },
  mainContainer: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.color.gray3}`,
  },
  editedForm: {
    border: `3px solid ${theme.color.blueLight}`,
  },
  spinnerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  recordCode: {
    fontWeight: "bold",
    alignSelf: "center",
  },
  titlesContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  deliveryDate: {
    height: 40,
    display: "flex",
    alignItems: "center",
  },
  opTableContainer: {
    margin: "16px 0",
  },
  tableHead: {
    borderRadius: "3px 3px 0px 0px",
    backgroundColor: theme.color.gray1,
  },
  opTableHeadCell: {
    "&.MuiTableCell-head": {
      color: theme.color.gray5,
      fontFamily: "Work Sans, sans-serif",
      fontSize: 12,
      textTransform: "uppercase",
      padding: "4px 12px 10px 12px !important",
    },
  },
  subRow: {
    cursor: "default",
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
    "&:hover": {
      background: "white",
    },
  },
  opParcelTitle: {
    padding: 8,
    fontSize: 15,
    fontWeight: "bold",
  },
  parcelTable: {
    padding: "0 40px 40px",
  },
  totalParcelContainer: {
    display: "flex",
    margin: "16px 0",
  },
  totalParcelTitle: {
    fontWeight: "bold",
    flex: 1,
  },
  totalParcelTable: {
    flex: 6,
    "&.MuiTableContainer-root": {
      display: "block",
      minHeight: 0,
    },
  },
  cell: {
    fontSize: 15,
    "&.MuiTableCell-root": {
      fontSize: 15,
    },
  },
  disabledCell: {
    color: theme.color.gray3,
    fontSize: 15,
  },
  notes: {
    display: "flex",
    margin: "16px 0",
  },
  notesField: {
    fontFamily: ` ${theme.font.workSans}`,
    flex: 1,
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  textButtonsContainer: {
    display: "flex",
    alignItems: "self-start",
    // Add styling for buttons within this container
    "& button": {
      backgroundColor: "transparent !important",
      border: "none !important",
      color: "black !important",
      "&:hover": {
        backgroundColor: `${theme.color.gray1} !important`, // or use '#f5f5f5' if gray1 is not available
      },
      "&:focus": {
        backgroundColor: `${theme.color.gray1} !important`,
      },
    },
  },
  textAlignStart: {
    textAlign: "start",
  },
  textButton: {
    backgroundColor: "transparent !important",
    border: "none !important",
    color: "black !important",
    "&:hover": {
      backgroundColor: `${theme.color.gray1} !important`, // light gray hover
    },
    "&:focus": {
      backgroundColor: `${theme.color.gray1} !important`,
    },
  },
  errorField: {
    minHeight: 20,
    color: theme.color.red,
    fontSize: 12,
  },
}));
